import { observable, action, computed, runInAction } from "mobx";
import { PersonalDataResponseModel } from "@data-types";
import { UserStatus } from "@api-types/user/LoginResponseDto";
import PersonalDataService from "@services/account-settings/PersonalDataService";
import { getDefaultCountryCodeForTheme, isNullOrWhitespace } from "@utils";

import RootStore from "../RootStore";
import { LoaderStore } from "../common";
import { THEME_SETTINGS } from "@theme-settings";
import { PERSONAL_DATA_SETTINGS } from "@personal-data-settings";
import { localizationService } from "@state/services";
import { logger } from "@utils/logger/logger";
import { FieldValues } from "react-hook-form";
import { PlayerPromoSendingMode } from "@data-types/membership/registration";
import { removeUndefinedFields } from "@utils/account-settings/personal-data";

export default class PersonalDataStore {
	rootStore: RootStore;
	loader: LoaderStore;

	@observable isStoreInitialized: boolean = false;
	@observable fetchedData: PersonalDataResponseModel | null = null;

	constructor(rootStore: RootStore) {
		this.rootStore = rootStore;
		this.loader = new LoaderStore();
	}

	@computed
	get isLoading(): boolean {
		return this.loader.isLoading || !this.isStoreInitialized;
	}

	@computed
	get isInsicEnabled() {
		return THEME_SETTINGS.isInsicEnabled;
	}

	@computed
	get isGamingSummaryEnabled() {
		return THEME_SETTINGS.isGamingSummaryEnabled;
	}

	@computed
	get isPersonalNumberEnabled() {
		return THEME_SETTINGS.isPersonalNumberEnabled;
	}

	@computed
	get isUserInternetOrMatchedUser() {
		if (
			this.rootStore.userAuthStore.isInternetUser ||
			(this.rootStore.userAuthStore.user &&
				this.rootStore.userAuthStore.secondaryUser)
		) {
			return true;
		} else {
			return false;
		}
	}

	@computed
	get defaultCountryCode() {
		return getDefaultCountryCodeForTheme();
	}

	@action.bound
	async fetchPersonalData() {
		this.loader.suspend();
		try {
			const response = await PersonalDataService.getPersonalData(
				PERSONAL_DATA_SETTINGS.fetchLookups,
				PERSONAL_DATA_SETTINGS.fetchAnnualIncomeLookups,
				PERSONAL_DATA_SETTINGS.showStateField
			);
			runInAction(() => {
				if (response) {
					this.fetchedData = response;
				}
			});
		} catch (error) {
			logger.logError(error);
			this.rootStore.notificationStore.error(
				localizationService.t(
					"USER.ACCOUNT_SETTINGS.ERR_HANDLING." + error?.message
				)
			);
		} finally {
			this.isStoreInitialized = true;
			this.loader.resume();
		}
	}

	@action.bound
	async updatePersonalData(formValues: FieldValues, password: string) {
		try {
			this.loader.suspend();

			if (!this.fetchedData) {
				throw "Expected fetchedData, got null";
			}

			const formData = removeUndefinedFields(
				formValues,
				this.fetchedData.data
			);

			let receiveNewsletter;

			if ("receiveNewsletter" in formData) {
				receiveNewsletter =
					//@ts-expect-error
					formData.receiveNewsletter == PlayerPromoSendingMode.Enabled
						? 1
						: 0;
			}

			const response = await PersonalDataService.updatePersonalData({
				...formData,
				//@ts-expect-error
				receiveNewsletter,
				password: password,
			});

			runInAction(() => {
				if (response != null && this.fetchedData) {
					this.fetchedData = {
						...this.fetchedData,
						data: { ...response },
					};
					this.rootStore.notificationStore.success(
						localizationService.t(
							"personal-data:PERSONAL_DATA_UPDATE_SUCCESS"
						)
					);
				}
			});
			return true;
		} catch (error) {
			console.log(error);
			if (error?.message) {
				this.rootStore.notificationStore.error(
					localizationService.t(
						"USER.ACCOUNT_SETTINGS.ERR_HANDLING." + error?.message
					)
				);
			} else {
				this.rootStore.notificationStore.error(
					localizationService.t(
						"USER.ACCOUNT_SETTINGS.ERR_HANDLING.GENERIC_ERROR"
					)
				);
			}
			return false;
		} finally {
			this.loader.resume();
		}
	}

	@action.bound
	async updateUserPin(currPayPin: string) {
		return PersonalDataService.updateUserPin(currPayPin);
	}

	@computed
	get isFieldDisabled() {
		if (this.isUserInternetOrMatchedUser) {
			if (
				this.fetchedData?.data?.insicStatus !== UserStatus.NotChecked ||
				this.fetchedData?.data?.insicStatus == null
			) {
				return true;
			} else {
				return false;
			}
		}
	}

	@computed
	get isCleanCard() {
		return !this.isUserInternetOrMatchedUser;
	}

	@computed
	get isTitleDisabled() {
		return this.isFieldDisabled || this.isCleanCard;
	}

	@computed
	get isFirstNameDisabled() {
		return this.isFieldDisabled || this.isCleanCard;
	}

	@computed
	get isLastNameDisabled() {
		return this.isFieldDisabled || this.isCleanCard;
	}

	@computed
	get isDobDisabled() {
		return this.isFieldDisabled || this.isCleanCard;
	}

	@computed
	get isStreetDisabled() {
		return this.isFieldDisabled || this.isCleanCard;
	}

	@computed
	get isCityDisabled() {
		return this.isFieldDisabled || this.isCleanCard;
	}

	@computed
	get isZipCodeDisabled() {
		return this.isFieldDisabled || this.isCleanCard;
	}

	@computed
	get isCountryIdDisabled() {
		return this.isFieldDisabled || this.isCleanCard;
	}

	@computed
	get isBirthNameDisabled() {
		return (
			this.isFieldDisabled ||
			(this.isCleanCard &&
				!isNullOrWhitespace(this.fetchedData?.data.birthName))
		);
	}

	@computed
	get isBirthPlaceDisabled() {
		return (
			this.isFieldDisabled ||
			(this.isCleanCard &&
				!isNullOrWhitespace(this.fetchedData?.data.birthPlace))
		);
	}

	@computed
	get isHouseNumberDisabled() {
		return (
			this.isFieldDisabled ||
			(this.isCleanCard &&
				!isNullOrWhitespace(this.fetchedData?.data.houseNumber))
		);
	}

	@computed
	get isNationalityDisabled() {
		return (
			this.isFieldDisabled ||
			(this.isCleanCard &&
				!isNullOrWhitespace(this.fetchedData?.data.nationality))
		);
	}

	@computed
	get isRegionIdDisabled() {
		return (
			this.isFieldDisabled ||
			(this.isCleanCard &&
				!isNullOrWhitespace(this.fetchedData?.data.regionId))
		);
	}

	@computed
	get isPersonalIdentificatiorDisabled() {
		return this.isFieldDisabled;
	}

	@computed
	get isEmailFieldDisabled() {
		return true;
	}

	@computed
	get isUsernameDisabled() {
		return true;
	}

	@computed
	get shouldFetchLookups() {
		return PERSONAL_DATA_SETTINGS.fetchLookups;
	}

	@computed
	get shouldFetchAnnualIncomeLookups() {
		return PERSONAL_DATA_SETTINGS.fetchAnnualIncomeLookups;
	}

	get shouldShowBirthNameField() {
		if (PERSONAL_DATA_SETTINGS.showBirthNameField) {
			return true;
		}

		return false;
	}

	@computed
	get shouldShowBirthPlaceField() {
		return (
			this.isInsicEnabled &&
			PERSONAL_DATA_SETTINGS.showBirthPlaceField &&
			!this.isPersonalNumberEnabled
		);
	}

	@computed
	get shouldShowPersonalNumber() {
		return this.isPersonalNumberEnabled;
	}

	@computed
	get shouldShowStateField() {
		return (
			this.isInsicEnabled &&
			!this.isPersonalNumberEnabled &&
			PERSONAL_DATA_SETTINGS.showStateField
		);
	}

	@computed
	get shouldShowOtherSection() {
		return (
			(this.isUserInternetOrMatchedUser &&
				this.isInsicEnabled &&
				!this.isPersonalNumberEnabled) ||
			PERSONAL_DATA_SETTINGS.showOtherSection
		);
	}

	@computed
	get shouldDisplayLugasId() {
		if (!PERSONAL_DATA_SETTINGS.showLugasId) {
			return false;
		}
		if (!this.fetchedData?.data?.displayUserId) {
			return false;
		}
		return true;
	}

	@computed
	get useAlternativePhoneInput() {
		return !this.isInsicEnabled;
	}

	@action
	shouldDisableEmptyField(
		value: Parameters<typeof isNullOrWhitespace>[0]
	): boolean {
		if (!this.isInsicEnabled) {
			return false;
		}
		if (!this.isFieldDisabled) {
			return false;
		} else if (isNullOrWhitespace(value)) {
			return false;
		}
		return true;
	}

	@computed
	get isPhoneValid() {
		const isInsicEnabled = this.isInsicEnabled;

		if (!isInsicEnabled) {
			return true;
		}

		return !isNullOrWhitespace(this.fetchedData?.data?.mobilePhone);
	}

	@computed
	get isPhoneInputDisabled() {
		return !THEME_SETTINGS.isInsicEnabled
			? false
			: !isNullOrWhitespace(this.fetchedData?.data?.mobilePhone);
	}

	@computed
	get shouldShowDocumentExpiration() {
		return PERSONAL_DATA_SETTINGS.showDocumentExpirationField;
	}

	@computed
	get isDocumentExpirationDisabled() {
		const expireDate = this.fetchedData?.data?.documentExpiration;
		if (!expireDate) return false;

		const { DateTime } = require("luxon");
		return (
			DateTime.fromISO(expireDate).endOf("day").toISO() >
			DateTime.now().endOf("day").toISO()
		);
	}

	@computed
	get isDocumentExpirationReadOnly() {
		return this.isDocumentExpirationDisabled;
	}

	@computed
	get shouldShowAnnualIncome() {
		return PERSONAL_DATA_SETTINGS.showAnnualIncomeField;
	}

	@computed
	get insicStatus() {
		return this.fetchedData?.data?.insicStatus;
	}

	@computed
	get shouldShowRecheckRequiredLink() {
		return this.insicStatus === UserStatus.RecheckRequired;
	}

	@computed
	get shouldShowVerificationStatus() {
		return (
			this.isUserInternetOrMatchedUser &&
			this.insicStatus != null &&
			this.insicStatus !== UserStatus.RecheckRequired
		);
	}

	@computed
	get shouldShowNationalityField() {
		return (
			PERSONAL_DATA_SETTINGS.showNationalityField &&
			this.isUserInternetOrMatchedUser
		);
	}
}
